// PE-6332 — Signed-in portal assistant panel (Triarq UI)
import { AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, Renderer2, ViewChild, inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RecaptchaModule } from 'ng-recaptcha-2';
import { environment } from '../../../environments/environment';
import { PatientChatChoice, PatientChatReply, PatientChatSuggestion } from '../../models/patient-chat.model';
import { AppointmentChatActionService } from '../../services/appointment-chat-action.service';
import { PatientChatService, PatientChatTranscriptLine } from '../../services/patient-chat.service';

// PE-6332 — body class that makes the page leave room for the docked panel (styles.scss)
const DOCKED_BODY_CLASS = 'patient-chat-docked';

@Component({
  selector: 'app-patient-chat',
  imports: [CommonModule, IonicModule, RecaptchaModule],
  templateUrl: './patient-chat.component.html',
  styleUrls: ['./patient-chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PatientChatComponent implements AfterViewChecked, OnDestroy {
  @ViewChild('thread') thread?: ElementRef<HTMLElement>;

  open = false;
  draft = '';
  sending = false;
  choices: PatientChatChoice[] = [];
  showCaptcha = false;
  readonly recaptchaSiteKey = environment.recaptchaSiteKey;
  lines: PatientChatTranscriptLine[] = [];
  readonly suggestions: PatientChatSuggestion[] = [
    { label: 'Next appointment', question: 'When is my next appointment?' },
    { label: 'Messages', question: 'Show my messages' },
    { label: 'Forms', question: 'Show my forms' },
    { label: 'Statements', question: 'Show my statements' },
    { label: 'Book a visit', question: 'I want to book an appointment' },
    // PE-6332 — same wording the classifier already treats as cancel / reschedule
    { label: 'Cancel appointment', question: 'Cancel my appointment' },
    { label: 'Reschedule appointment', question: 'Reschedule my appointment' },
    { label: 'Lab results', question: 'Where are my results?' },
  ];

  private nextId = 2;
  private lastScrollMarker = '';
  private readonly destroy$ = new Subject<void>();
  private readonly chatService = inject(PatientChatService);
  private readonly appointmentChatAction = inject(AppointmentChatActionService);
  private readonly router = inject(Router);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);

  constructor() {
    this.restoreTranscript();
  }

  // PE-6332 — scroll after the new line / typing indicator is in the DOM
  ngAfterViewChecked(): void {
    this.scrollLatestIntoView();
  }

  ngOnDestroy(): void {
    this.persistTranscript();
    this.renderer.removeClass(this.document.body, DOCKED_BODY_CLASS);
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggle(): void {
    this.setOpen(!this.open);
    this.lastScrollMarker = '';
  }

  onDraftInput(event: Event): void {
    const target = event.target;
    this.draft = target instanceof HTMLInputElement ? target.value : '';
  }

  sendDraft(): void {
    this.ask(this.draft);
  }

  // PE-6332 — booking option selected from the assistant list
  choose(choice: PatientChatChoice): void {
    if (this.sending) {
      return;
    }
    this.choices = [];
    this.showCaptcha = false;
    this.pushLine({ from: 'patient', text: choice.label });
    this.sending = true;
    this.chatService
      .selectChoice(choice.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (reply) => this.finishReply(reply),
        error: () => this.finishReply(this.fallbackReply()),
      });
  }

  // PE-6332 — captcha is required before the existing book API saves the visit
  onCaptcha(token: string | null): void {
    if (!token || this.sending) {
      return;
    }
    this.showCaptcha = false;
    this.sending = true;
    this.chatService
      .confirmBooking(token)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (reply) => this.finishReply(reply),
        error: () => this.finishReply(this.fallbackReply()),
      });
  }

  ask(question: string): void {
    const text = question.trim();
    if (!text || this.sending) {
      return;
    }
    this.draft = '';
    this.choices = [];
    this.showCaptcha = false;
    this.pushLine({ from: 'patient', text });
    this.sending = true;
    this.chatService
      .ask(text)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (reply) => this.finishReply(reply),
        error: () => this.finishReply(this.fallbackReply()),
      });
  }

  // PE-6332 — clear the thread and any booking in progress
  startOver(): void {
    if (this.sending) {
      return;
    }
    this.chatService.clearTranscript();
    this.restoreTranscript();
    this.lastScrollMarker = '';
    this.changeDetector.markForCheck();
  }

  openRoute(route: string | undefined): void {
    if (!route || this.sending) {
      return;
    }
    const appointmentAction = readChatAppointmentAction(route);
    const onAppointmentsPage = (this.router.url ?? '').split('?')[0] === '/appointments';
    // PE-6332 — already on Appointments: open that visit without reloading the list
    if (appointmentAction && onAppointmentsPage) {
      this.appointmentChatAction.request(appointmentAction);
      this.setOpen(false);
      return;
    }
    void this.router.navigateByUrl(route);
    this.setOpen(false);
  }

  // PE-6332 — keep the page layout in step with the panel so it docks beside the page, not over it
  private setOpen(open: boolean): void {
    this.open = open;
    this.syncDockedClass();
    this.persistTranscript();
    this.changeDetector.markForCheck();
  }

  // PE-6332 — closing the panel only hides it; the thread stays until Start over
  private restoreTranscript(): void {
    const saved = this.chatService.readTranscript();
    this.open = saved.open;
    this.lines = saved.lines;
    this.choices = saved.choices;
    this.draft = saved.draft;
    this.showCaptcha = saved.showCaptcha;
    this.nextId = saved.nextId;
    this.syncDockedClass();
  }

  private persistTranscript(): void {
    this.chatService.rememberTranscript({
      open: this.open,
      lines: this.lines,
      choices: this.choices,
      draft: this.draft,
      showCaptcha: this.showCaptcha,
      nextId: this.nextId,
    });
  }

  private syncDockedClass(): void {
    if (this.open) {
      this.renderer.addClass(this.document.body, DOCKED_BODY_CLASS);
    } else {
      this.renderer.removeClass(this.document.body, DOCKED_BODY_CLASS);
    }
  }

  trackLine(_index: number, line: PatientChatTranscriptLine): number {
    return line.id;
  }

  private finishReply(reply: PatientChatReply): void {
    this.sending = false;
    this.choices = reply.choices ?? [];
    this.showCaptcha = reply.requiresCaptcha === true;
    this.pushLine({
      from: 'assistant',
      text: reply.text,
      route: reply.route,
      routeLabel: reply.routeLabel,
    });
  }

  private fallbackReply(): PatientChatReply {
    return {
      text: 'I could not answer that just now. You can open Messages or call the office.',
      route: '/messages',
      routeLabel: 'Open messages',
    };
  }

  private pushLine(line: Omit<PatientChatTranscriptLine, 'id'>): void {
    this.lines = [...this.lines, { ...line, id: this.nextId }];
    this.nextId += 1;
    this.persistTranscript();
    this.changeDetector.markForCheck();
  }

  private scrollLatestIntoView(): void {
    const element = this.thread?.nativeElement;
    if (!element || !this.open) {
      return;
    }
    // PE-6332: include sending so typing indicator scrolls into view
    const marker = `${this.lines.length}:${this.choices.length}:${this.showCaptcha}:${this.sending}`;
    if (marker === this.lastScrollMarker) {
      return;
    }
    this.lastScrollMarker = marker;
    element.scrollTop = element.scrollHeight;
  }
}

function readChatAppointmentAction(route: string): { action: 'cancel' | 'reschedule'; apptId: string } | null {
  const queryIndex = route.indexOf('?');
  if (queryIndex < 0 || route.slice(0, queryIndex) !== '/appointments') {
    return null;
  }
  const params = new URLSearchParams(route.slice(queryIndex + 1));
  const action = params.get('chatAction');
  const apptId = (params.get('apptId') ?? '').trim();
  if ((action === 'cancel' || action === 'reschedule') && /^[A-Za-z0-9-]+$/.test(apptId)) {
    return { action, apptId };
  }
  return null;
}
